import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarDays, CalendarOff, ClipboardList, Clock, CreditCard, FileEdit, Gift, Images, LayoutDashboard, LogOut, Megaphone, Package, Pencil, Quote, Receipt, RefreshCw, Send, Star, Trash2, UserCog, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { formatTime12 } from "../lib/data";
import logoMark from "../assets/releaf_mark.png";
import FormBuilderTab from "../admin/FormBuilderTab";
import InventoryTab from "../admin/InventoryTab";
import SalesTab from "../admin/SalesTab";
import TeamTab from "../admin/TeamTab";
import { UsageModal } from "../admin/UsageModal";
import BannerTab from "../admin/BannerTab";
import GiftsTab from "../admin/GiftsTab";
import MembershipsTab from "../admin/MembershipsTab";
import PatientsTab from "../admin/PatientsTab";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS_STYLES = {
  booked: "bg-gold/20 text-gold-dark border-gold/40",
  confirmed: "bg-forest/10 text-forest border-forest/30",
  completed: "bg-sage/20 text-moss border-sage/40",
  cancelled: "bg-destructive/10 text-destructive border-destructive/30",
};

function formatDay(dateStr) {
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function OverviewTab({ goTo }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((res) => setData(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!data) return <p className="text-sm text-moss">Loading overview...</p>;

  const stats = [
    { id: "today", label: "Today's Visits", value: data.stats.today_count, tab: "bookings" },
    { id: "week", label: "Next 7 Days", value: data.stats.week_count, tab: "bookings" },
    { id: "forms", label: "New Form Submissions", value: data.stats.new_forms, tab: "forms" },
    { id: "pending", label: "Awaiting Confirmation", value: data.stats.pending_bookings, tab: "bookings" },
  ];

  const Row = ({ b, showDate }) => (
    <li data-testid={`overview-booking-${b.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-linen bg-ivory px-5 py-3.5">
      <div className="flex items-center gap-4">
        <span className="w-20 shrink-0 font-serif text-lg text-forest">{formatTime12(b.time)}</span>
        <div>
          <p className="font-medium text-forest">{b.name}</p>
          <p className="text-xs text-moss">{b.service_name}{showDate && ` · ${formatDay(b.date)}`}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[b.status] || ""}`}>{b.status}</span>
        <span className="hidden text-xs text-moss sm:block">{b.visit_type_label} &middot; ${b.price}</span>
      </div>
    </li>
  );

  return (
    <div data-testid="overview-tab">
      <h2 className="font-serif text-2xl text-forest">
        Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, Nichole
      </h2>
      <p className="mt-1 text-sm text-moss">{new Date(`${data.today}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <button
            key={s.id}
            data-testid={`overview-stat-${s.id}`}
            onClick={() => goTo(s.tab)}
            className="rounded-lg border border-linen bg-ivory p-5 text-left transition-colors duration-300 hover:border-gold"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">{s.label}</p>
            <p className="mt-2 font-serif text-4xl text-forest">{s.value}</p>
          </button>
        ))}
      </div>

      <div className="mt-10">
        <h3 className="font-serif text-xl text-forest">Today&rsquo;s Schedule</h3>
        {data.today_bookings.length === 0 ? (
          <p data-testid="overview-today-empty" className="mt-4 rounded-md border border-linen bg-cream p-5 text-sm text-moss">No appointments today.</p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="overview-today-list">
            {data.today_bookings.map((b) => <Row key={b.id} b={b} />)}
          </ul>
        )}
      </div>

      <div className="mt-10">
        <h3 className="font-serif text-xl text-forest">Coming Up This Week</h3>
        {data.upcoming.length === 0 ? (
          <p data-testid="overview-upcoming-empty" className="mt-4 rounded-md border border-linen bg-cream p-5 text-sm text-moss">Nothing scheduled in the next 7 days.</p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="overview-upcoming-list">
            {data.upcoming.slice(0, 8).map((b) => <Row key={b.id} b={b} showDate />)}
          </ul>
        )}
        {data.upcoming.length > 8 && (
          <button data-testid="overview-see-all" onClick={() => goTo("bookings")} className="mt-4 text-sm font-semibold text-forest transition-colors hover:text-gold-dark">
            See all bookings &rarr;
          </button>
        )}
      </div>
    </div>
  );
}

function BookingsTab({ onSendForm }) {
  const [bookings, setBookings] = useState(null);
  const [usageFor, setUsageFor] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/bookings").then((res) => setBookings(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked as ${status}`);
      if (status === "completed") setUsageFor(bookings.find((b) => b.id === id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onLogged = (usage) => {
    setBookings((prev) => prev.map((b) => (b.id === usageFor.id ? { ...b, usage } : b)));
    setUsageFor(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-forest">Bookings</h2>
        <button data-testid="bookings-refresh-button" onClick={load} className="flex items-center gap-2 rounded-md border border-linen px-4 py-2 text-sm font-medium text-forest transition-colors hover:border-gold">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {!bookings && <p className="mt-8 text-sm text-moss">Loading bookings...</p>}
      {bookings && bookings.length === 0 && (
        <p data-testid="bookings-empty" className="mt-8 rounded-md border border-linen bg-cream p-6 text-sm text-moss">
          No bookings yet. New appointments will appear here.
        </p>
      )}
      {bookings && bookings.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-lg border border-linen" data-lenis-prevent>
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="bookings-table">
            <thead className="bg-cream text-xs font-semibold uppercase tracking-[0.15em] text-moss">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Forms</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linen bg-ivory">
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-forest">{formatDay(b.date)}</p>
                    <p className="text-xs text-moss">{formatTime12(b.time)} &middot; {b.duration} min &middot; {b.visit_type_label}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-forest">{b.name}</p>
                    <p className="text-xs text-moss">{b.email}</p>
                    <p className="text-xs text-moss">{b.phone}</p>
                    {b.notes && <p className="mt-1 max-w-[220px] truncate text-xs italic text-moss/70" title={b.notes}>{b.notes}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-forest">{b.service_name}</p>
                    <p className="text-xs text-moss">${b.price}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full border px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[b.status] || ""}`} data-testid={`booking-status-${b.id}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-moss">
                    {b.forms_sent?.length ? `${b.forms_sent.length} sent (${b.forms_sent[b.forms_sent.length - 1].status})` : "—"}
                    {b.forms_completed?.length ? <span className="block font-semibold text-forest">{b.forms_completed.length} completed</span> : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        data-testid={`booking-status-select-${b.id}`}
                        value={b.status}
                        onChange={(e) => setStatus(b.id, e.target.value)}
                        className="rounded-md border border-linen bg-ivory px-2 py-1.5 text-xs outline-none focus:border-gold"
                      >
                        {["booked", "confirmed", "completed", "cancelled"].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <button
                        data-testid={`booking-send-form-${b.id}`}
                        onClick={() => onSendForm(b)}
                        className="flex items-center gap-1.5 rounded-md bg-forest px-3 py-1.5 text-xs font-semibold text-ivory transition-colors hover:bg-forest-dark"
                      >
                        <Send size={12} /> Email Form
                      </button>
                      {b.status === "completed" && (
                        <button
                          data-testid={`booking-log-usage-${b.id}`}
                          onClick={() => setUsageFor(b)}
                          className="flex items-center gap-1.5 rounded-md border border-linen px-3 py-1.5 text-xs font-semibold text-forest transition-colors hover:border-forest"
                          title={b.usage?.length ? b.usage.map((u) => `${u.qty} ${u.unit} ${u.item_name}`).join(", ") : "Log product used"}
                        >
                          <Package size={12} /> {b.usage?.length ? `${b.usage.length} logged` : "Log Product"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {usageFor && <UsageModal booking={usageFor} onClose={() => setUsageFor(null)} onLogged={onLogged} />}
    </div>
  );
}

function AvailabilityTab() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/admin/availability").then((res) => {
      const map = {};
      res.data.forEach((r) => { map[r.weekday] = r; });
      setRules(map);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const update = (weekday, patch) => setRules((prev) => ({ ...prev, [weekday]: { ...prev[weekday], ...patch } }));

  const save = async (weekday) => {
    setSaving(weekday);
    try {
      const r = rules[weekday];
      await api.put(`/admin/availability/${weekday}`, {
        weekday,
        start: r.start,
        end: r.end,
        slot_minutes: Number(r.slot_minutes),
        active: Boolean(r.active),
      });
      toast.success(`${WEEKDAYS[weekday]} saved`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(null);
    }
  };

  if (!rules) return <p className="text-sm text-moss">Loading availability...</p>;

  return (
    <div>
      <h2 className="font-serif text-2xl text-forest">Weekly Availability</h2>
      <p className="mt-2 text-sm text-moss">Set the hours clients can book for each day of the week. Changes apply immediately.</p>
      <div className="mt-6 space-y-3">
        {WEEKDAYS.map((name, weekday) => {
          const r = rules[weekday] || { start: "09:00", end: "17:00", slot_minutes: 30, active: false };
          return (
            <div key={weekday} data-testid={`availability-row-${weekday}`} className={`flex flex-wrap items-center gap-4 rounded-lg border p-4 transition-colors ${r.active ? "border-linen bg-ivory" : "border-linen/60 bg-cream/60"}`}>
              <label className="flex w-36 items-center gap-3">
                <input
                  type="checkbox"
                  data-testid={`availability-active-${weekday}`}
                  checked={Boolean(r.active)}
                  onChange={(e) => update(weekday, { active: e.target.checked })}
                  className="h-4 w-4 accent-[#1A3626]"
                />
                <span className="font-medium text-forest">{name}</span>
              </label>
              <div className="flex items-center gap-2 text-sm text-moss">
                <input type="time" data-testid={`availability-start-${weekday}`} value={r.start} disabled={!r.active} onChange={(e) => update(weekday, { start: e.target.value })} className="rounded-md border border-linen bg-ivory px-2 py-1.5 outline-none focus:border-gold disabled:opacity-40" />
                <span>to</span>
                <input type="time" data-testid={`availability-end-${weekday}`} value={r.end} disabled={!r.active} onChange={(e) => update(weekday, { end: e.target.value })} className="rounded-md border border-linen bg-ivory px-2 py-1.5 outline-none focus:border-gold disabled:opacity-40" />
              </div>
              <select data-testid={`availability-slot-${weekday}`} value={r.slot_minutes} disabled={!r.active} onChange={(e) => update(weekday, { slot_minutes: e.target.value })} className="rounded-md border border-linen bg-ivory px-2 py-1.5 text-sm outline-none focus:border-gold disabled:opacity-40">
                {[15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{m}-min slots</option>)}
              </select>
              <button
                data-testid={`availability-save-${weekday}`}
                onClick={() => save(weekday)}
                disabled={saving === weekday}
                className="ml-auto rounded-md bg-forest px-4 py-1.5 text-xs font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50"
              >
                {saving === weekday ? "Saving..." : "Save"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeOffTab() {
  const [blocked, setBlocked] = useState(null);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    api.get("/admin/blocked-dates").then((res) => setBlocked(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blocked-dates", { date, reason });
      toast.success("Date blocked");
      setDate("");
      setReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/admin/blocked-dates/${d}`);
      toast.success("Date unblocked");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl text-forest">Time Off &amp; Blocked Dates</h2>
      <p className="mt-2 text-sm text-moss">Block full days — vacations, holidays, or personal time. Blocked days disappear from the booking calendar.</p>

      <form onSubmit={add} className="mt-6 flex flex-wrap items-end gap-3 rounded-lg border border-linen bg-cream p-4">
        <div>
          <label htmlFor="blocked-date" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Date</label>
          <input id="blocked-date" data-testid="blocked-date-input" type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 block rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <div className="min-w-[200px] flex-1">
          <label htmlFor="blocked-reason" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Reason (optional)</label>
          <input id="blocked-reason" data-testid="blocked-reason-input" type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Vacation, holiday..." className="mt-1 block w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <button type="submit" data-testid="blocked-add-button" className="rounded-md bg-forest px-5 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark">
          Block Date
        </button>
      </form>

      {!blocked && <p className="mt-6 text-sm text-moss">Loading...</p>}
      {blocked && blocked.length === 0 && <p data-testid="blocked-empty" className="mt-6 text-sm text-moss">No blocked dates.</p>}
      {blocked && blocked.length > 0 && (
        <ul className="mt-6 space-y-2">
          {blocked.map((b) => (
            <li key={b.date} data-testid={`blocked-row-${b.date}`} className="flex items-center justify-between rounded-md border border-linen bg-ivory px-4 py-3">
              <div>
                <span className="font-medium text-forest">{formatDay(b.date)}</span>
                {b.reason && <span className="ml-3 text-sm text-moss">{b.reason}</span>}
              </div>
              <button data-testid={`blocked-delete-${b.date}`} onClick={() => remove(b.date)} className="flex items-center gap-1 text-sm font-medium text-destructive transition-colors hover:opacity-70">
                <X size={14} /> Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState(null);
  const [treatment, setTreatment] = useState("");
  const [caption, setCaption] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const resolve = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  const load = useCallback(() => {
    api.get("/gallery").then((res) => setItems(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(load, [load]);

  const upload = async (e) => {
    e.preventDefault();
    const formEl = e.target;
    const fd = new FormData();
    fd.append("treatment", treatment);
    fd.append("caption", caption);
    fd.append("before", beforeFile);
    fd.append("after", afterFile);
    setBusy(true);
    try {
      await api.post("/admin/gallery", fd);
      toast.success("Photos added to the gallery");
      setTreatment("");
      setCaption("");
      setBeforeFile(null);
      setAfterFile(null);
      formEl.reset();
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this before/after pair from the public gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Removed from gallery");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl text-forest">Before &amp; After Gallery</h2>
      <p className="mt-2 text-sm text-moss">Upload real before/after photos. They appear instantly in the &ldquo;Results&rdquo; section of the website. Delete the placeholder examples once real photos are in.</p>

      <form onSubmit={upload} className="mt-6 grid gap-4 rounded-lg border border-linen bg-cream p-5 sm:grid-cols-2">
        <div>
          <label htmlFor="gallery-treatment" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Treatment</label>
          <input id="gallery-treatment" data-testid="gallery-treatment-input" type="text" required value={treatment} onChange={(e) => setTreatment(e.target.value)} placeholder="Lip Filler, Botox..." className="mt-1 block w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label htmlFor="gallery-caption" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Caption (optional)</label>
          <input id="gallery-caption" data-testid="gallery-caption-input" type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Short description of the result" className="mt-1 block w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label htmlFor="gallery-before" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Before photo</label>
          <input id="gallery-before" data-testid="gallery-before-input" type="file" required accept="image/jpeg,image/png,image/webp" onChange={(e) => setBeforeFile(e.target.files[0] || null)} className="mt-1 block w-full text-sm text-moss file:mr-3 file:rounded-md file:border-0 file:bg-forest file:px-4 file:py-2 file:text-xs file:font-semibold file:text-ivory" />
        </div>
        <div>
          <label htmlFor="gallery-after" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">After photo</label>
          <input id="gallery-after" data-testid="gallery-after-input" type="file" required accept="image/jpeg,image/png,image/webp" onChange={(e) => setAfterFile(e.target.files[0] || null)} className="mt-1 block w-full text-sm text-moss file:mr-3 file:rounded-md file:border-0 file:bg-forest file:px-4 file:py-2 file:text-xs file:font-semibold file:text-ivory" />
        </div>
        <button type="submit" data-testid="gallery-upload-button" disabled={busy || !beforeFile || !afterFile} className="rounded-md bg-forest px-5 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50 sm:col-span-2 sm:justify-self-start">
          {busy ? "Uploading..." : "Add to Gallery"}
        </button>
      </form>

      {!items && <p className="mt-6 text-sm text-moss">Loading gallery...</p>}
      {items && items.length === 0 && <p data-testid="gallery-empty" className="mt-6 text-sm text-moss">The gallery is empty. Upload your first before/after pair above.</p>}
      {items && items.length > 0 && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <div key={item.id} data-testid={`gallery-admin-item-${item.id}`} className="rounded-lg border border-linen bg-ivory p-4">
              <div className="grid grid-cols-2 gap-2">
                <img src={resolve(item.before_url)} alt={`${item.treatment} before`} className="aspect-square w-full rounded-md object-cover" loading="lazy" />
                <img src={resolve(item.after_url)} alt={`${item.treatment} after`} className="aspect-square w-full rounded-md object-cover" loading="lazy" />
              </div>
              <div className="mt-3 flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-forest">{item.treatment}</p>
                  {item.caption && <p className="mt-0.5 text-xs text-moss">{item.caption}</p>}
                  {item.is_placeholder && <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gold-dark">Placeholder</span>}
                </div>
                <button data-testid={`gallery-delete-${item.id}`} onClick={() => remove(item.id)} className="flex items-center gap-1 text-xs font-medium text-destructive transition-colors hover:opacity-70">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_TESTIMONIAL = { name: "", treatment: "", quote: "", rating: 5 };

function TestimonialsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY_TESTIMONIAL);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/testimonials").then((res) => setItems(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/admin/testimonials/${editingId}`, { ...form, rating: Number(form.rating) });
        toast.success("Testimonial updated");
      } else {
        await api.post("/admin/testimonials", { ...form, rating: Number(form.rating) });
        toast.success("Testimonial added");
      }
      setForm(EMPTY_TESTIMONIAL);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, treatment: t.treatment, quote: t.quote, rating: t.rating });
    window.scrollTo(0, 0);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this testimonial from the website?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Testimonial removed");
      if (editingId === id) {
        setEditingId(null);
        setForm(EMPTY_TESTIMONIAL);
      }
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl text-forest">Client Testimonials</h2>
      <p className="mt-2 text-sm text-moss">Reviews shown in the &ldquo;Kind Words&rdquo; section of the website. Replace the sample testimonials with real client reviews as they come in.</p>

      <form onSubmit={submit} className="mt-6 grid gap-4 rounded-lg border border-linen bg-cream p-5 sm:grid-cols-2">
        <div>
          <label htmlFor="testimonial-name" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Client name</label>
          <input id="testimonial-name" data-testid="testimonial-name-input" type="text" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Amanda R." className="mt-1 block w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label htmlFor="testimonial-treatment" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Treatment (optional)</label>
          <input id="testimonial-treatment" data-testid="testimonial-treatment-input" type="text" value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} placeholder="Botox, Lip Filler..." className="mt-1 block w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="testimonial-quote" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Quote</label>
          <textarea id="testimonial-quote" data-testid="testimonial-quote-input" required minLength={10} rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="What the client said..." className="mt-1 block w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
        <div>
          <label htmlFor="testimonial-rating" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Rating</label>
          <select id="testimonial-rating" data-testid="testimonial-rating-select" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-1 block rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-gold">
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-3">
          <button type="submit" data-testid="testimonial-save-button" disabled={busy} className="rounded-md bg-forest px-5 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50">
            {busy ? "Saving..." : editingId ? "Update Testimonial" : "Add Testimonial"}
          </button>
          {editingId && (
            <button type="button" data-testid="testimonial-cancel-edit" onClick={() => { setEditingId(null); setForm(EMPTY_TESTIMONIAL); }} className="rounded-md border border-linen px-4 py-2.5 text-sm font-medium text-moss transition-colors hover:border-gold">
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {!items && <p className="mt-6 text-sm text-moss">Loading testimonials...</p>}
      {items && items.length === 0 && <p data-testid="testimonials-empty" className="mt-6 text-sm text-moss">No testimonials yet. Add your first one above.</p>}
      {items && items.length > 0 && (
        <ul className="mt-6 space-y-3">
          {items.map((t) => (
            <li key={t.id} data-testid={`testimonial-admin-item-${t.id}`} className={`rounded-lg border bg-ivory p-5 ${editingId === t.id ? "border-gold" : "border-linen"}`}>
              <p className="font-serif text-forest">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-forest">{t.name}</span>
                  {t.treatment && <span className="text-xs uppercase tracking-[0.15em] text-gold-dark">{t.treatment}</span>}
                  <span className="flex items-center gap-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={12} className="fill-gold text-gold" />)}
                  </span>
                  {t.is_placeholder && <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gold-dark">Sample</span>}
                </div>
                <div className="flex items-center gap-4">
                  <button data-testid={`testimonial-edit-${t.id}`} onClick={() => startEdit(t)} className="flex items-center gap-1 text-xs font-medium text-forest transition-colors hover:text-gold-dark">
                    <Pencil size={13} /> Edit
                  </button>
                  <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t.id)} className="flex items-center gap-1 text-xs font-medium text-destructive transition-colors hover:opacity-70">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SubmissionModal({ submission, onClose, onReviewed }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/50 p-4" onClick={onClose}>
      <div data-testid="submission-modal" className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-linen bg-ivory p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-lenis-prevent>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl text-forest">{submission.form_name}</h3>
            <p className="mt-1 text-sm text-moss">{submission.name} &middot; {submission.email}{submission.phone && ` · ${submission.phone}`}</p>
            {submission.booking_service && <p className="mt-0.5 text-xs text-moss">Booking: {submission.booking_service}</p>}
          </div>
          <button data-testid="submission-modal-close" onClick={onClose} className="text-moss transition-colors hover:text-forest"><X size={18} /></button>
        </div>
        <dl className="mt-6 space-y-4">
          {Object.entries(submission.answers || {}).map(([key, value]) => (
            <div key={key} className="rounded-md border border-linen bg-cream p-4">
              <dt className="text-[10px] font-semibold uppercase tracking-[0.15em] text-moss">{key.replace(/_/g, " ")}</dt>
              <dd className="mt-1 text-sm text-forest">
                {typeof value === "boolean" ? (value ? "Yes — acknowledged" : "No") : String(value || "—")}
              </dd>
            </div>
          ))}
        </dl>
        <div className="mt-5 rounded-md border border-gold/40 bg-cream p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-moss">Signature</p>
          <p className="mt-1 font-serif text-2xl italic text-forest">{submission.signature}</p>
          <p className="mt-1 text-xs text-moss">Signed {new Date(submission.submitted_at).toLocaleString("en-US")}</p>
        </div>
        {submission.status === "new" && (
          <button data-testid="submission-mark-reviewed" onClick={onReviewed} className="mt-6 w-full rounded-md bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark">
            Mark as Reviewed
          </button>
        )}
      </div>
    </div>
  );
}

function FormsTab() {
  const [submissions, setSubmissions] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/form-submissions").then((res) => setSubmissions(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(load, [load]);

  const markReviewed = async () => {
    try {
      await api.patch(`/admin/form-submissions/${selected.id}`, { status: "reviewed" });
      toast.success("Marked as reviewed");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-forest">Form Submissions</h2>
        <button data-testid="forms-refresh-button" onClick={load} className="flex items-center gap-2 rounded-md border border-linen px-4 py-2 text-sm font-medium text-forest transition-colors hover:border-gold">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      <p className="mt-2 text-sm text-moss">Intake, consent, cannabis, and HRT forms completed online by clients.</p>

      {!submissions && <p className="mt-6 text-sm text-moss">Loading submissions...</p>}
      {submissions && submissions.length === 0 && (
        <p data-testid="forms-empty" className="mt-6 rounded-md border border-linen bg-cream p-6 text-sm text-moss">
          No submissions yet. When a client completes a form online, it will appear here.
        </p>
      )}
      {submissions && submissions.length > 0 && (
        <ul className="mt-6 space-y-2">
          {submissions.map((s) => (
            <li key={s.id} data-testid={`submission-row-${s.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-linen bg-ivory px-5 py-4">
              <div>
                <p className="font-medium text-forest">{s.form_name}</p>
                <p className="text-xs text-moss">{s.name} &middot; {s.email} &middot; {new Date(s.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize ${s.status === "new" ? "bg-gold/20 text-gold-dark border-gold/40" : "bg-sage/20 text-moss border-sage/40"}`}>
                  {s.status}
                </span>
                <button data-testid={`submission-view-${s.id}`} onClick={() => setSelected(s)} className="rounded-md bg-forest px-4 py-1.5 text-xs font-semibold text-ivory transition-colors hover:bg-forest-dark">
                  View
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {selected && <SubmissionModal submission={selected} onClose={() => setSelected(null)} onReviewed={markReviewed} />}
    </div>
  );
}

function SendFormModal({ booking, onClose }) {
  const [forms, setForms] = useState([]);
  const [formType, setFormType] = useState("");
  const [recipient, setRecipient] = useState(booking.email);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/forms").then((res) => {
      setForms(res.data);
      if (res.data[0]) setFormType(res.data[0].form_type);
    });
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/send-form`, {
        form_type: formType,
        message,
        recipient,
      });
      if (data.status === "mocked") {
        toast.info(`Recorded as a draft — email delivery isn't configured yet (MOCKED).`);
      } else {
        toast.success(`Form emailed to ${data.recipient}`);
      }
      onClose(true);
    } catch (e) {
      toast.error(apiError(e, "Could not send the form."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/50 p-4" onClick={() => onClose(false)}>
      <div data-testid="send-form-modal" className="w-full max-w-md rounded-lg border border-linen bg-ivory p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl text-forest">Email a form</h3>
            <p className="mt-1 text-sm text-moss">to {booking.name} — they&rsquo;ll get a link to fill it out online</p>
          </div>
          <button data-testid="send-form-close" onClick={() => onClose(false)} className="text-moss transition-colors hover:text-forest"><X size={18} /></button>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="form-type" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Form</label>
            <select id="form-type" data-testid="send-form-type" value={formType} onChange={(e) => setFormType(e.target.value)} className="mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2.5 text-sm outline-none focus:border-gold">
              {forms.map((f) => <option key={f.form_type} value={f.form_type}>{f.title}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="form-recipient" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Recipient email</label>
            <input id="form-recipient" data-testid="send-form-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2.5 text-sm outline-none focus:border-gold" />
          </div>
          <div>
            <label htmlFor="form-message" className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Personal message (optional)</label>
            <textarea id="form-message" data-testid="send-form-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Please complete this before your visit..." className="mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2.5 text-sm outline-none focus:border-gold" />
          </div>
        </div>

        <button
          data-testid="send-form-submit"
          onClick={send}
          disabled={busy || !recipient || !formType}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50"
        >
          <Send size={14} /> {busy ? "Sending..." : "Send Form"}
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [formBooking, setFormBooking] = useState(null);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="font-serif text-xl text-forest">Loading...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === "patient") return <Navigate to="/portal" replace />;
  const isOwner = user.role === "admin";

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "bookings", label: "Bookings", icon: CalendarDays },
    { id: "sales", label: "Sales", icon: Receipt },
    { id: "inventory", label: "Inventory", icon: Package },
    { id: "forms", label: "Form Inbox", icon: ClipboardList },
    { id: "formbuilder", label: "Form Builder", icon: FileEdit },
    { id: "patients", label: "Patients", icon: Users },
    { id: "memberships", label: "Memberships", icon: CreditCard },
    { id: "gifts", label: "Gift Cards", icon: Gift },
    { id: "banner", label: "Specials Banner", icon: Megaphone },
    { id: "availability", label: "Availability", icon: Clock },
    { id: "timeoff", label: "Time Off", icon: CalendarOff },
    { id: "gallery", label: "Gallery", icon: Images },
    { id: "testimonials", label: "Testimonials", icon: Quote },
    ...(isOwner ? [{ id: "team", label: "Team", icon: UserCog }] : []),
  ];

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen bg-cream">
      <aside className="hidden w-60 shrink-0 flex-col bg-forest text-ivory lg:flex">
        <div className="flex items-center gap-3 border-b border-ivory/10 p-6">
          <span className="flex h-11 w-12 shrink-0 items-center justify-center rounded-md bg-ivory p-1">
            <img src={logoMark} alt="Releaf monogram" className="h-full w-auto" />
          </span>
          <span>
            <span className="block font-serif text-xl">Releaf</span>
            <span className="block text-[9px] font-semibold uppercase tracking-[0.3em] text-gold">Provider Portal</span>
          </span>
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex w-full items-center gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors duration-300 ${tab === t.id ? "bg-forest-dark text-gold" : "text-ivory/70 hover:bg-forest-dark/60 hover:text-ivory"}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-ivory/10 p-4">
          <p className="truncate px-2 text-xs text-ivory/50">{user.name || user.email}{!isOwner && " · Staff"}</p>
          <button data-testid="admin-logout-button" onClick={logout} className="mt-2 flex w-full items-center gap-3 rounded-md px-4 py-3 text-sm font-medium text-ivory/70 transition-colors hover:bg-forest-dark/60 hover:text-ivory">
            <LogOut size={16} /> Sign Out
          </button>
          <Link to="/" data-testid="admin-view-site" className="mt-1 block px-4 py-2 text-xs text-ivory/50 transition-colors hover:text-gold">
            &larr; View website
          </Link>
        </div>
      </aside>

      <div className="flex-1">
        <div className="flex items-center justify-between border-b border-linen bg-ivory px-5 py-4 lg:hidden">
          <span className="font-serif text-lg text-forest">Releaf Portal</span>
          <div className="flex flex-wrap items-center justify-end gap-1">
            {tabs.map((t) => (
              <button key={t.id} data-testid={`admin-tab-mobile-${t.id}`} onClick={() => setTab(t.id)} className={`rounded-md p-2 ${tab === t.id ? "bg-forest text-gold" : "text-moss"}`} aria-label={t.label}>
                <t.icon size={18} />
              </button>
            ))}
            <button data-testid="admin-logout-mobile" onClick={logout} className="rounded-md p-2 text-moss" aria-label="Sign out"><LogOut size={18} /></button>
          </div>
        </div>

        <main className="p-5 sm:p-8">
          {tab === "overview" && <OverviewTab goTo={setTab} />}
          {tab === "bookings" && <BookingsTab onSendForm={setFormBooking} />}
          {tab === "sales" && <SalesTab />}
          {tab === "inventory" && <InventoryTab />}
          {tab === "team" && isOwner && <TeamTab />}
          {tab === "forms" && <FormsTab />}
          {tab === "formbuilder" && <FormBuilderTab />}
          {tab === "patients" && <PatientsTab />}
          {tab === "memberships" && <MembershipsTab />}
          {tab === "gifts" && <GiftsTab />}
          {tab === "banner" && <BannerTab />}
          {tab === "availability" && <AvailabilityTab />}
          {tab === "timeoff" && <TimeOffTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "testimonials" && <TestimonialsTab />}
        </main>
      </div>

      {formBooking && (
        <SendFormModal
          booking={formBooking}
          onClose={(sent) => {
            setFormBooking(null);
            if (sent) setTab("bookings");
          }}
        />
      )}
    </div>
  );
}
